/**
 * Cookie consent management using CookieYes
 * Dynamically reads cookie categories from CookieYes configuration
 */

/**
 * Cookie consent categories matching CookieYes
 */
export enum ConsentCategory {
  NECESSARY = 'necessary',
  ANALYTICS = 'analytics',
  PERFORMANCE = 'performance',
  FUNCTIONAL = 'functional',
  ADVERTISING = 'advertisement',
  OTHER = 'other',
}

/**
 * CookieYes category interface from their store
 */
interface CookieYesCategory {
  slug: string;
  isNecessary: boolean;
  defaultConsent: {
    gdpr: boolean;
    ccpa: boolean;
  };
  cookies: Array<{
    cookieID: string;
    domain: string;
  }>;
}

/**
 * CookieYes internal store interface
 */
interface CookieYesStore {
  _categories?: CookieYesCategory[];
  _providersToBlock?: Array<{
    url: string;
    categories: string[];
    fullPath: boolean;
  }>;
}

/**
 * CookieYes consent state interface
 */
interface CookieYesConsent {
  necessary: boolean;
  analytics: boolean;
  performance: boolean;
  functional: boolean;
  advertisement: boolean;
  other: boolean;
}

/**
 * Checks if CookieYes is loaded
 */
export function isCookieYesLoaded(): boolean {
  return typeof window !== 'undefined' && typeof window.cookieyes !== 'undefined';
}

/**
 * Gets CookieYes store with category and cookie information
 */
export function getCookieYesStore(): CookieYesStore | null {
  if (typeof window === 'undefined' || !window.cookieyes) {
    return null;
  }

  return window.cookieyes._ckyStore || null;
}

/**
 * Dynamically builds cookie category map from CookieYes configuration
 * This replaces the hardcoded COOKIE_CATEGORIES constant
 */
export function getCookieCategoriesMap(): Record<string, ConsentCategory> {
  const store = getCookieYesStore();
  const categoryMap: Record<string, ConsentCategory> = {};

  if (!store || !store._categories) {
    return categoryMap;
  }

  // Build map from CookieYes categories
  for (const category of store._categories) {
    const categorySlug = category.slug as ConsentCategory;

    // Map each cookie to its category
    for (const cookie of category.cookies || []) {
      categoryMap[cookie.cookieID] = categorySlug;
    }
  }

  return categoryMap;
}

/**
 * Gets current consent state from CookieYes
 */
export function getConsentState(): CookieYesConsent {
  if (typeof window === 'undefined') {
    // SSR - allow all (cookies not accessible anyway)
    return {
      necessary: true,
      analytics: true,
      performance: true,
      functional: true,
      advertisement: true,
      other: true,
    };
  }

  // If CookieYes not loaded yet, return conservative defaults
  if (!isCookieYesLoaded()) {
    return {
      necessary: true,
      analytics: false,
      performance: false,
      functional: false,
      advertisement: false,
      other: false,
    };
  }

  // Read from CookieYes store
  try {
    // CookieYes stores consent in their _ckyConsentStore Map or in cookies
    const consentFromStore = window.cookieyes?._ckyGetFromStore;

    if (consentFromStore) {
      return {
        necessary: true, // Always true
        analytics: consentFromStore('analytics') === 'yes',
        performance: consentFromStore('performance') === 'yes',
        functional: consentFromStore('functional') === 'yes',
        advertisement: consentFromStore('advertisement') === 'yes',
        other: consentFromStore('other') === 'yes',
      };
    }

    // Fallback: Parse from cookie
    const consentCookie = document.cookie.split('; ').find((row) => row.startsWith('cookieyes-consent='));

    if (consentCookie) {
      const consentData = consentCookie.split('=')[1];
      const consentPairs = consentData.split(',').reduce<Record<string, string>>((acc, pair) => {
        const [key, value] = pair.split(':');
        if (key) acc[key] = value;
        return acc;
      }, {});

      return {
        necessary: true,
        analytics: consentPairs.analytics === 'yes',
        performance: consentPairs.performance === 'yes',
        functional: consentPairs.functional === 'yes',
        advertisement: consentPairs.advertisement === 'yes',
        other: consentPairs.other === 'yes',
      };
    }

    // No consent data found
    return {
      necessary: true,
      analytics: false,
      performance: false,
      functional: false,
      advertisement: false,
      other: false,
    };
  } catch (error) {
    console.warn('[CookieConsent] Failed to read consent:', error);
    return {
      necessary: true,
      analytics: false,
      performance: false,
      functional: false,
      advertisement: false,
      other: false,
    };
  }
}

/**
 * Checks if consent is granted for a category
 */
export function hasConsent(category: ConsentCategory): boolean {
  const consent = getConsentState();
  return consent[category];
}

/**
 * Checks if consent is granted for a specific cookie
 * Uses dynamic category map from CookieYes configuration
 */
export function hasConsentForCookie(cookieName: string): boolean {
  // Get dynamic category map
  const categoryMap = getCookieCategoriesMap();
  const category = categoryMap[cookieName];

  // If cookie not categorized in CookieYes, treat as necessary (fail-safe)
  if (!category) {
    return true;
  }

  return hasConsent(category);
}

// TypeScript declarations for CookieYes global API
declare global {
  interface Window {
    cookieyes?: {
      _ckyStore?: CookieYesStore;
      _ckyGetFromStore?: (key: string) => string;
      _ckySetInStore?: (key: string, value: string) => void;
      _ckyConsentStore?: Map<string, string>;
    };
  }
}
